import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

interface User {
    id: string;
    email: string;
    role?: string;
    full_name?: string;
}

interface AuthContextType {
    user: User | null;
    isAdmin: boolean;
    loading: boolean;
    hasActiveSubscription: boolean;
    /** plan_type do perfil (free, pro, lifetime, etc.). Admin = null (tratado como pago). */
    planType: string | null;
    /** true quando planType === 'free' (apenas Dashboard liberado). */
    isFreePlan: boolean;
    refreshSubscription: () => Promise<void>;
    login: (email: string, password: string, isAdminLogin?: boolean) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [planType, setPlanType] = useState<string | null>(null);

    const fetchMe = useCallback(async () => {
        const token = localStorage.getItem('hotlize_token');
        if (!token) return;
        try {
            const res = await api.get<{ user: User; profile: { plan_type?: string } | null; hasActiveSubscription: boolean }>('/me');
            if (res.data?.user) {
                setUser(res.data.user);
                setHasActiveSubscription(Boolean(res.data.hasActiveSubscription));
                const pt = res.data.profile?.plan_type ?? null;
                setPlanType(typeof pt === 'string' ? pt : null);
                localStorage.setItem('hotlize_user', JSON.stringify(res.data.user));
            }
        } catch {
            setUser(null);
            setHasActiveSubscription(false);
            setPlanType(null);
            localStorage.removeItem('hotlize_user');
            localStorage.removeItem('hotlize_token');
            localStorage.removeItem('hotlize_refresh_token');
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('hotlize_token');
        if (token) {
            fetchMe().finally(() => setLoading(false));
        } else {
            const savedUser = localStorage.getItem('hotlize_user');
            if (savedUser) {
                localStorage.removeItem('hotlize_user');
                localStorage.removeItem('hotlize_refresh_token');
            }
            setLoading(false);
        }
    }, [fetchMe]);

    const login = async (email: string, password: string, isAdminLogin = false) => {
        let res;
        try {
            res = await api.post('/auth/platform-login', { email, password });
        } catch (err: any) {
            const status = err.response?.status;
            if (status === 401) throw new Error('E-mail ou senha incorretos.');

            if (status === 503 || status === 502) throw new Error('Servidor temporariamente indispon\u00edvel. Tente novamente.');
            const isNetworkError = !err.response && (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error'));
            if (isNetworkError) {
                throw new Error('Backend não está acessível. Verifique se o servidor está rodando (npm run dev ou npm start na pasta do projeto).');
            }
            throw new Error(err.response?.data?.error || 'N\u00e3o foi poss\u00edvel conectar ao servidor.');
        }

        if (res.data.success) {
            const userData = res.data.user;
            const isAdminUser = userData?.role === 'admin' || res.data.token != null;
            if (isAdminUser) userData.role = 'admin';

            const token = isAdminLogin ? res.data.token : (res.data.session?.access_token || res.data.token);
            const tokenStr = token != null ? String(token).trim() : '';

            // Admin exige token; sem token as requisições futuras falham com "Token não fornecido"
            if (isAdminUser && !tokenStr) {
                throw new Error('Login admin indisponível. Configure ADMIN_MASTER_TOKEN no servidor.');
            }

            if (tokenStr) {
                localStorage.setItem('hotlize_token', tokenStr);
            }
            const refreshToken = res.data.session?.refresh_token;
            if (refreshToken) {
                localStorage.setItem('hotlize_refresh_token', refreshToken);
            }

            setUser(userData);
            localStorage.setItem('hotlize_user', JSON.stringify(userData));

            if (isAdminUser) {
                setHasActiveSubscription(true);
                setPlanType(null);
            } else {
                try {
                    const meRes = await api.get<{ hasActiveSubscription: boolean; profile?: { plan_type?: string } }>('/me');
                    setHasActiveSubscription(Boolean(meRes.data?.hasActiveSubscription));
                    const pt = meRes.data?.profile?.plan_type ?? null;
                    setPlanType(typeof pt === 'string' ? pt : null);
                } catch {
                    setHasActiveSubscription(false);
                    setPlanType(null);
                }
            }
        } else {
            throw new Error(res.data.error || 'N\u00e3o foi poss\u00edvel realizar o login. Verifique suas credenciais.');
        }
    };

    const logout = () => {
        setUser(null);
        setHasActiveSubscription(false);
        setPlanType(null);
        localStorage.removeItem('hotlize_user');
        localStorage.removeItem('hotlize_token');
        localStorage.removeItem('hotlize_refresh_token');
    };

    const isAdmin = user?.email === 'hotlize@admin.com' || user?.role === 'admin';
    const isFreePlan = !isAdmin && planType === 'free';

    return (
        <AuthContext.Provider value={{ user, isAdmin, loading, hasActiveSubscription, planType, isFreePlan, refreshSubscription: fetchMe, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
